import { onAuthStateChanged, type User } from "firebase/auth";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { Reset } from "./auth/Reset";
import { SecondFactorGate } from "./auth/SecondFactorGate";
import { SignIn } from "./auth/SignIn";
import { auth } from "./firebase.config";
import { BrokenLanding } from "./landing/BrokenLanding";
import { Shell } from "./shell/Shell";

/**
 * Routing model:
 *   /             → BrokenLanding (decoy — looks like a Chrome error page).
 *                   The trailing "." in the error code links to /portal.
 *   /portal       → SignIn  (the real auth page; bland, no branding).
 *                   Authenticated users hitting /portal redirect to /app.
 *   /app/...      → Shell (the actual platform), behind two factors:
 *                   1. Firebase email+password (handled at /portal).
 *                   2. SecondFactorGate — facial recognition, which holds an
 *                      authenticated user until their face verifies/enrolls.
 *                   Unauthenticated requests redirect to /portal.
 */

export function App() {
  const user = useAuthUser();

  return (
    <Routes>
      <Route path="/" element={<BrokenLanding />} />

      <Route path="/reset" element={<Reset />} />

      <Route
        path="/portal"
        element={
          user === undefined ? null : user ? <Navigate to="/app" replace /> : <SignIn />
        }
      />

      <Route
        path="/app/*"
        element={
          user === undefined ? null : user ? (
            <SecondFactorGate>
              <Shell />
            </SecondFactorGate>
          ) : (
            <Navigate to="/portal" replace />
          )
        }
      />

      {/* Anything else → broken page (no link discovery for guessed URLs) */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function useAuthUser(): User | null | undefined {
  const [u, setU] = useState<User | null | undefined>(undefined);
  useEffect(() => onAuthStateChanged(auth, setU), []);
  return u;
}
